import type { AccessLevel } from '@/interfaces/Access.interface';
import { apiDateFormatToUnzonedDateTime, type UnzonedDateTime } from '@/interfaces/Date.interface';
import { CoreApiVersionPath, RosaCoreApiService } from '@/rosa';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, map, type Observable } from 'rxjs';
import type { AlertScenario, AlertScenarioId, AlertScenarioStatus, AlertScenarioType, AlertStatPeriodCode } from '../alerts.interface';

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreAlertScenariosService {
    private _rosaCoreApiService = inject(RosaCoreApiService);

    deleteAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return firstValueFrom(this._rosaCoreApiService.delete<void>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}`));
    }

    getAlertScenario$(alertScenarioId: AlertScenarioId): Observable<AlertScenario> {
        interface RouteResponse {
            alertScenarioId: AlertScenarioId;
            scenarioType: AlertScenarioType;
            scenarioStatus: AlertScenarioStatus;
            name: string;
            access: AccessLevel;
            periodInfos: {
                isActiveInOccupation: boolean;
                isActiveInInoccupation: boolean;
                isActiveDuringSchoolHolidays: boolean;
                isActiveDuringPublicHolidays: boolean;
                periodStart: {
                    day: number;
                    month: number;
                };
                periodEnd: {
                    day: number;
                    month: number;
                };
                daysRanges: Record<
                    string,
                    {
                        isActivated: boolean;
                        ranges: {
                            start: string;
                            end: string;
                        }[];
                    }
                >;
            };
        }

        return this._rosaCoreApiService.get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenario/${alertScenarioId}`).pipe(
            map(s => ({
                scenarioId: s.alertScenarioId,
                type: s.scenarioType,
                status: s.scenarioStatus,
                name: s.name,
                access: s.access
            }))
        );
    }

    setAlertScenarioEnabled(alertScenarioId: AlertScenarioId, scenarioType: AlertScenarioType, enabled: boolean): Promise<AlertScenarioId> {
        interface RouteRequest {
            enabled: boolean;
            scenarioType: AlertScenarioType;
        }

        const enabledScenarioAlert: RouteRequest = {
            enabled: enabled,
            scenarioType: scenarioType
        };

        return firstValueFrom(
            this._rosaCoreApiService.post<AlertScenarioId>(
                `${CoreApiVersionPath.V5}alerts/scenario/${alertScenarioId}/enabled`,
                enabledScenarioAlert
            )
        );
    }

    cancelAlertScenarioEdition(): Promise<void> {
        return firstValueFrom(this._rosaCoreApiService.post<void>(`${CoreApiVersionPath.V5}alerts/scenario/session/cancel`, {}));
    }

    alertStatPeriod$(periodCode: AlertStatPeriodCode): Observable<{ startDate: UnzonedDateTime; endDate: UnzonedDateTime }> {
        interface RouteResponse {
            startDate: string;
            endDate: string;
        }

        return this._rosaCoreApiService.get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/periods/${periodCode}`).pipe(
            map(response => ({
                startDate: apiDateFormatToUnzonedDateTime(response.startDate),
                endDate: apiDateFormatToUnzonedDateTime(response.endDate)
            }))
        );
    }
}
